import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import { api, apiError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui';
import { downloadFromResponse, describeDownloadError } from '../lib/download';
import { artifactIdFromHref, remarkArtifactReferences, setArtifactEditorContent } from '../lib/artifactLinks';
import './ReportWorkspace.css';
import { SingleOutput } from './RunOutputs';
import { safeMarkdownComponents } from './SafeMarkdown';

const WRITING = new Set(['preparing', 'generating', 'reviewing']);
const LABELS = { preparing: 'Подготовка структуры', generating: 'Написание документа', reviewing: 'Проверка',
  ready: 'Готово', superseded: 'Предыдущий черновик', partial: 'Есть ограничения', stopped: 'Генерация остановлена', edited: 'Правки пользователя' };
const extensions = [StarterKit.configure({ link: { HTMLAttributes: { tabindex: '0' } } }), TableKit, Markdown];
const MIN_DOCUMENT_WIDTH = 420;
const MIN_CHAT_WIDTH = 300;

export function DocumentReader({ title, content }) {
  return <article className="report-reader" aria-label={title || 'Документ'}>
    {title && <h2>{title}</h2>}
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={safeMarkdownComponents}>{content || ''}</ReactMarkdown>
  </article>;
}

export default function ReportWorkspace({ documents, activeId, onActivate, onClose, onOpen, sessionId, readOnly = false, embedded = false }) {
  const { user } = useAuth();
  const active = documents.find(d => d.id === activeId) || documents[documents.length - 1];
  const visible = !!active;
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const preferredWidth = useRef(null);
  const currentWidth = useRef(null);
  const [width, setWidth] = useState(null);
  const [maximumWidth, setMaximumWidth] = useState(MIN_DOCUMENT_WIDTH);
  const [resizing, setResizing] = useState(false);
  const widthKey = user?.id ? `rnd-report-width:${user.id}` : null;

  const fitWidth = useCallback(requested => {
    if (!panelRef.current || !panelRef.current.getClientRects().length || window.matchMedia('(max-width:1000px)').matches) return;
    const available = (embedded ? panelRef.current.closest('.session-body') : panelRef.current.parentElement).getBoundingClientRect().width;
    const maximum = Math.max(MIN_DOCUMENT_WIDTH, Math.floor(available - MIN_CHAT_WIDTH));
    const next = Math.round(Math.min(maximum, Math.max(MIN_DOCUMENT_WIDTH, requested)));
    currentWidth.current = next;
    setMaximumWidth(maximum);
    setWidth(next);
    if (embedded) panelRef.current.closest('.session-auxiliary').style.setProperty('--session-auxiliary-width', `${next}px`);
    return next;
  }, [embedded]);

  const rememberWidth = useCallback(() => {
    if (widthKey && currentWidth.current !== null) {
      try { localStorage.setItem(widthKey, String(currentWidth.current)); }
      catch { console.warn('report.workspace_width_storage_unavailable'); }
    }
    console.info('report.workspace_resized', { width: currentWidth.current });
  }, [widthKey]);

  useEffect(() => {
    if (!visible || !panelRef.current) return undefined;
    preferredWidth.current = null;
    if (widthKey) {
      try {
        const saved = Number(localStorage.getItem(widthKey));
        if (Number.isFinite(saved) && saved >= MIN_DOCUMENT_WIDTH) preferredWidth.current = saved;
      } catch { console.warn('report.workspace_width_storage_unavailable'); }
    }
    const fit = () => fitWidth(preferredWidth.current ?? Math.min(window.innerWidth * 0.58, 1000));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(embedded ? panelRef.current.closest('.session-body') : panelRef.current.parentElement);
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [fitWidth, visible, widthKey, embedded]);

  const finishResize = event => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setResizing(false);
    rememberWidth();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  if (!active) return null;
  return <aside ref={panelRef} className={'report-workspace' + (embedded ? ' report-workspace-embedded' : '') + (resizing ? ' is-resizing' : '')}
    style={width === null ? undefined : { '--report-workspace-width': `${width}px` }} aria-label="Документы сессии">
    <div className="report-resize-handle" role="separator" tabIndex={0} aria-orientation="vertical"
      aria-label="Изменить ширину панели документов" aria-valuemin={MIN_DOCUMENT_WIDTH}
      aria-valuemax={maximumWidth} aria-valuenow={width ?? MIN_DOCUMENT_WIDTH}
      title="Потяните для изменения ширины. Двойной щелчок — исходная ширина."
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        dragRef.current = { pointerId: event.pointerId, x: event.clientX,
          width: panelRef.current.getBoundingClientRect().width };
        event.currentTarget.setPointerCapture(event.pointerId);
        setResizing(true);
      }}
      onPointerMove={event => {
        const drag = dragRef.current;
        if (drag?.pointerId !== event.pointerId) return;
        preferredWidth.current = fitWidth(drag.width + drag.x - event.clientX);
      }}
      onPointerUp={finishResize} onPointerCancel={finishResize} onLostPointerCapture={finishResize}
      onDoubleClick={() => {
        preferredWidth.current = null;
        fitWidth(Math.min(window.innerWidth * 0.58, 1000));
        rememberWidth();
      }}
      onKeyDown={event => {
        const next = { ArrowLeft: width + 20, ArrowRight: width - 20, Home: MIN_DOCUMENT_WIDTH, End: maximumWidth }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        preferredWidth.current = fitWidth(next);
        rememberWidth();
      }} />
    <div className="report-document-tabs" role="tablist" aria-label="Открытые документы">
      {documents.map(d => <div key={d.id} className={'report-document-tab' + (active.id === d.id ? ' selected' : '')}>
        <button role="tab" aria-selected={active.id === d.id} onClick={() => onActivate(d.id)}>{d.title}</button>
        <button aria-label={`Закрыть ${d.title}`} onClick={() => onClose(d.id)}>×</button>
      </div>)}
    </div>
    {documents.map(resource => <div className="report-document-content" key={`${resource.id}:${resource.revision || 'current'}`} hidden={active.id !== resource.id}>
      <DocumentPane resource={resource} onOpen={onOpen} sessionId={sessionId} readOnly={readOnly} onDenied={onClose} />
    </div>)}
  </aside>;
}

function DocumentPane({ resource, onOpen, sessionId, readOnly, onDenied }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const canEdit = !readOnly && !user?.moderation?.restricted && !doc?.outline?.output_id;
  const deniedCallback = useRef(onDenied);
  deniedCallback.current = onDenied;
  const refreshGeneration = useRef(0);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [history, setHistory] = useState(null);
  const [viewedRevision, setViewedRevision] = useState(resource.revision || null);
  const [busyExport, setBusyExport] = useState(null);
  const [link, setLink] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const remoteRevision = useRef(0);
  const historicalLoaded = useRef(false);
  const values = useRef({ title: '', content: '', revision: 1 });
  const dirtyRef = useRef(false);
  const inFlight = useRef(null);
  const mounted = useRef(true);
  const editorRef = useRef(null);
  const loaded = useRef(false);
  const navigating = useRef(false);
  const [openingReference, setOpeningReference] = useState(false);
  const isReport = resource.type === 'report';
  const cacheKey = `rnd-report-draft:${user?.id}:${resource.id}`;
  const writing = isReport && WRITING.has(doc?.status);

  const remember = useCallback(() => {
    try { localStorage.setItem(cacheKey, JSON.stringify(values.current)); } catch { /* current text remains in the editor */ }
  }, [cacheKey]);

  const editor = useEditor({ extensions, content: '', contentType: 'markdown', editable: false,
    editorProps: { attributes: { class: 'report-editor', role: 'textbox', 'aria-multiline': 'true', 'aria-label': 'Содержимое итогового документа' } },
    onUpdate: ({ editor: current }) => {
      values.current.content = current.getMarkdown();
      dirtyRef.current = true;
      setDirty(true);
      remember();
    },
  });
  editorRef.current = editor;

  const apply = useCallback(async (remote, force = false) => {
    if (!mounted.current) return;
    if (isReport && remote.revision < remoteRevision.current) return;
    remoteRevision.current = remote.revision || 0;
    setDoc(remote);
    if (!isReport) return;
    if (force || !loaded.current || WRITING.has(remote.status)) {
      let next = { title: remote.title, content: remote.content_markdown, revision: remote.revision };
      if (!loaded.current && !WRITING.has(remote.status)) {
        try {
          const cached = JSON.parse(localStorage.getItem(cacheKey));
          if (cached?.content && cached.content !== remote.content_markdown) {
            next = cached;
            dirtyRef.current = true;
            setDirty(true);
            setConflict(cached.revision !== remote.revision);
          }
        } catch { /* no recoverable local draft */ }
      }
      values.current = next;
      setTitleDraft(next.title);
      loaded.current = true;
      setArtifactEditorContent(editorRef.current, next.content);
    }
  }, [cacheKey, isReport]);

  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    try {
      const remote = isReport ? await api.getReportDocument(resource.id) : await api.getArtifact(resource.id);
      if (mounted.current && generation === refreshGeneration.current) await apply(remote);
    } catch (e) {
      if (!mounted.current || generation !== refreshGeneration.current) return;
      setError(apiError(e));
      if ([403, 404].includes(e?.response?.status)) {
        setDoc(null); values.current = { title: '', content: '', revision: 1 };
        dirtyRef.current = false; setDirty(false); setArtifactEditorContent(editorRef.current, '');
        localStorage.removeItem(cacheKey); deniedCallback.current?.(resource.id);
      }
    }
  }, [apply, isReport, resource.id, cacheKey]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = setInterval(refresh, 2000);
    return () => { mounted.current = false; clearInterval(timer); };
  }, [isReport, refresh]);

  useEffect(() => {
    if (!isReport) return undefined;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const base = new URL(api.baseURL || '/api', window.location.origin);
    const socket = new WebSocket(`${protocol}//${base.host}/ws/sessions/${sessionId}`);
    let timer;
    socket.onmessage = event => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'report_updated' && message.document_id === resource.id && !timer) {
          timer = setTimeout(() => { timer = null; refresh(); }, 200);
        }
      } catch { /* periodic snapshots recover missed events */ }
    };
    socket.onerror = () => { /* periodic snapshots remain active */ };
    return () => { clearTimeout(timer); socket.close(); };
  }, [isReport, refresh, resource.id, sessionId]);

  useEffect(() => {
    editor?.setEditable(canEdit && isReport && !!doc && !writing && !viewedRevision, false);
    if (editor && loaded.current && !editor.getText() && values.current.content) {
      setArtifactEditorContent(editor, values.current.content);
    }
  }, [canEdit, doc, editor, isReport, viewedRevision, writing]);

  useEffect(() => {
    if (!doc || !resource.revision || historicalLoaded.current) return;
    historicalLoaded.current = true;
    api.reportDocumentRevisions(resource.id).then(revisions => {
      if (!mounted.current) return;
      const revision = revisions.find(r => r.revision === resource.revision);
      if (revision) {
        values.current = { title: revision.title, content: revision.content_markdown, revision: doc.revision };
        setTitleDraft(revision.title);
        setArtifactEditorContent(editorRef.current, revision.content_markdown);
      }
    }).catch(e => setError(apiError(e)));
  }, [doc, resource.id, resource.revision]);

  const save = useCallback(async (copy = false) => {
    if (!canEdit) throw new Error("Документ доступен на чтение");
    if (inFlight.current) return inFlight.current;
    if (!dirtyRef.current && !copy) return doc;
    const sent = { ...values.current };
    setSaving(true);
    const request = api.saveReportDocument(resource.id, { title: sent.title, content_markdown: sent.content,
      expected_revision: sent.revision, save_as_copy: copy }).then(remote => {
      values.current.revision = remote.revision;
      remoteRevision.current = remote.revision;
      const changed = values.current.content !== sent.content || values.current.title !== sent.title;
      dirtyRef.current = changed;
      if (changed) remember(); else localStorage.removeItem(cacheKey);
      if (mounted.current) {
        setDoc(remote); setDirty(changed); setConflict(false); setError(null);
        if (copy) onOpen({ id: remote.id, type: 'report', title: remote.title });
      }
      return remote;
    }).catch(e => {
      if (mounted.current) { setError(apiError(e)); setConflict(e?.response?.status === 409); }
      throw e;
    }).finally(() => {
      inFlight.current = null;
      if (mounted.current) setSaving(false);
    });
    inFlight.current = request;
    return request;
  }, [canEdit, cacheKey, doc, onOpen, remember, resource.id]);

  const openArtifactReference = async artifactId => {
    if (navigating.current) return;
    navigating.current = true;
    setOpeningReference(true);
    setError(null);
    let stage = 'load';
    try {
      const target = await api.getArtifact(artifactId);
      if (!mounted.current) return;
      if (target.session_id !== sessionId) throw new Error('Документ относится к другой сессии.');
      stage = 'save';
      if (canEdit && isReport && !writing && !viewedRevision) {
        if (conflict) throw new Error('Разрешите конфликт редакций перед открытием другого документа.');
        // An autosave may already be in flight, or edits may arrive while
        // saving. Never leave until the latest content is durably saved.
        for (let attempt = 0; dirtyRef.current && attempt < 3; attempt += 1) await save();
        if (canEdit && dirtyRef.current) throw new Error('Дождитесь сохранения последних изменений.');
      }
      if (!mounted.current) return;
      onOpen({ id: target.id, type: 'artifact', title: target.title });
      console.info('document.artifact_reference_opened', { source_id: resource.id, artifact_id: target.id });
    } catch (e) {
      if (!mounted.current) return;
      const status = e?.response?.status;
      const message = e?.response ? apiError(e) : e.message || apiError(e);
      setError(stage === 'save' ? `Не удалось сохранить документ: ${message}`
        : status === 404 ? 'Документ по этой ссылке не найден.'
          : status === 403 ? 'Нет доступа к документу по этой ссылке.'
            : message);
      console.error('document.artifact_reference_failed', { source_id: resource.id, artifact_id: artifactId, stage, status });
    } finally {
      navigating.current = false;
      if (mounted.current) setOpeningReference(false);
    }
  };

  const handleReference = event => {
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    if (event.type === 'click' && event.button !== 0) return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor || !event.currentTarget.contains(anchor)) return;
    const artifactId = artifactIdFromHref(anchor.getAttribute('href'));
    if (!artifactId) return;
    event.preventDefault();
    event.stopPropagation();
    openArtifactReference(artifactId);
  };

  useEffect(() => {
    if (!canEdit || !dirty || conflict || writing || viewedRevision || !editor) return undefined;
    const timer = setTimeout(() => { save().catch(() => {}); }, 1000);
    return () => clearTimeout(timer);
  }, [canEdit, conflict, dirty, doc, editor, save, viewedRevision, writing]);

  useEffect(() => {
    const guard = event => { if (dirtyRef.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, []);

  const showRevision = async (revision, restore = false) => {
    const current = dirtyRef.current ? await save() : doc;
    values.current = { title: revision.title, content: revision.content_markdown, revision: current.revision };
    setTitleDraft(revision.title);
    setArtifactEditorContent(editor, revision.content_markdown);
    setViewedRevision(restore ? null : revision.revision);
    if (restore) { dirtyRef.current = true; setDirty(true); remember(); await save(); }
  };

  const download = async fmt => {
    setBusyExport(fmt); setError(null);
    try {
      let current = doc;
      if (canEdit && isReport && !viewedRevision && dirtyRef.current) current = await save();
      // If edits arrived during the save, include them as well.
      if (canEdit && isReport && !viewedRevision && dirtyRef.current) current = await save();
      if (canEdit && dirtyRef.current) throw new Error('Дождитесь сохранения последних изменений');
      const response = isReport ? await api.exportReportDocument(resource.id, fmt, viewedRevision || current.revision)
        : await api.exportArtifact(resource.id, fmt);
      downloadFromResponse(response, `${current.title}.${fmt}`);
    } catch (e) { setError(await describeDownloadError(e)); }
    finally { setBusyExport(null); }
  };

  if (!doc) return <div className="report-pane">{error || 'Загрузка документа…'}</div>;
  if (doc.outline?.output_id) return <div className="report-pane"><SingleOutput sessionId={sessionId} outputId={doc.outline.output_id} readOnly={readOnly || user?.moderation?.restricted} /></div>;
  const title = isReport ? titleDraft : doc.title;
  return <div className="report-pane" onClickCapture={handleReference} onKeyDownCapture={handleReference}>
    <div className="report-pane-heading">
      {canEdit && isReport && !writing && !viewedRevision ? <input aria-label="Название документа" value={title} onChange={e => {
        values.current.title = e.target.value; setTitleDraft(e.target.value); dirtyRef.current = true; setDirty(true); remember();
      }} /> : <h2>{title}</h2>}
      <span role="status">{isReport ? LABELS[doc.status] || doc.status : 'Документ исследования'}
        {isReport && !writing && ` · ${saving ? 'Сохраняется…' : dirty ? 'Есть несохранённые правки' : 'Сохранено'}`}</span>
    </div>
    {error && <div role="alert" className="alert alert-error">{error}</div>}
    {openingReference && <div role="status">Открытие документа…</div>}
    {canEdit && conflict && <div className="report-conflict">
      Документ изменён в другой вкладке. Ваши правки сохранены в этом браузере.
      <Button onClick={async () => { const remote = await api.getReportDocument(resource.id);
        localStorage.removeItem(cacheKey); dirtyRef.current = false; setDirty(false); setConflict(false); setError(null); await apply(remote, true); }}>Загрузить актуальную редакцию</Button>
      <Button onClick={() => save(true).catch(() => {})}>Сохранить мой вариант отдельно</Button>
    </div>}
    <div className="report-actions">
      {canEdit && writing && <Button onClick={async () => { await api.stopReportDocument(doc.id); await refresh(); }}>Остановить генерацию</Button>}
      {canEdit && isReport && !writing && !viewedRevision && <Button disabled={!dirty || saving || conflict} onClick={() => save().catch(() => {})}>Сохранить</Button>}
      {isReport && <Button onClick={async () => setHistory(history ? null : await api.reportDocumentRevisions(doc.id))}>История</Button>}
      {['pdf', 'docx', 'md'].map(fmt => <Button key={fmt} disabled={!!busyExport || writing || (isReport && !values.current.content)}
        onClick={() => download(fmt)}>{busyExport === fmt ? 'Подготовка…' : fmt === 'docx' ? 'Word' : fmt.toUpperCase()}</Button>)}
    </div>
    {history && <div className="report-history">{history.map(r => <div key={r.revision}>
      <span>Редакция {r.revision} · {r.author === 'agent' ? 'Агент' : 'Пользователь'} · {new Date(r.created_at).toLocaleString('ru-RU')}</span>
      <Button onClick={() => showRevision(r).catch(e => setError(e.message))}>Посмотреть</Button>
      {canEdit && !writing && <Button onClick={() => showRevision(r, true).catch(e => setError(e.message))}>Восстановить</Button>}
    </div>)}</div>}
    {viewedRevision && <div className="report-version-banner">Просмотр редакции {viewedRevision}
      <Button onClick={async () => { setViewedRevision(null); await apply(await api.getReportDocument(doc.id), true); }}>К текущей редакции</Button></div>}
    {canEdit && isReport && !writing && !viewedRevision && editor && <div className="report-toolbar" role="toolbar" aria-label="Форматирование">
      <Button onClick={() => editor.chain().focus().toggleBold().run()} aria-label="Полужирный">Ж</Button>
      <Button onClick={() => editor.chain().focus().toggleItalic().run()} aria-label="Курсив">К</Button>
      <Button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>Заголовок</Button>
      <Button onClick={() => editor.chain().focus().toggleBulletList().run()}>Список</Button>
      <Button onClick={() => editor.chain().focus().toggleOrderedList().run()}>Нумерация</Button>
      <Button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>Таблица</Button>
      <Button onClick={() => editor.chain().focus().addRowAfter().run()}>+ строка</Button>
      <Button onClick={() => editor.chain().focus().deleteRow().run()}>− строка</Button>
      <input type="url" aria-label="Адрес ссылки" placeholder="https://…" value={link} onChange={e => setLink(e.target.value)} />
      <Button disabled={!/^https?:\/\//i.test(link)} onClick={() => { editor.chain().focus().setLink({ href: link }).run(); setLink(''); }}>Ссылка</Button>
      <Button onClick={() => editor.chain().focus().undo().run()}>Отменить правку</Button>
      <Button onClick={() => editor.chain().focus().redo().run()}>Повторить правку</Button>
    </div>}
    {isReport ? <EditorContent editor={editor} /> : <div className="markdown report-reader">
      <ReactMarkdown components={safeMarkdownComponents} remarkPlugins={[remarkGfm, remarkArtifactReferences]}>{doc.reader_markdown || doc.content_markdown || JSON.stringify(doc.content_json, null, 2)}</ReactMarkdown>
    </div>}
    {writing && <div className="report-progress" aria-live="polite">{doc.sections?.filter(s => s.status === 'done').length || 0} из {doc.sections?.length || '…'} разделов готово</div>}
  </div>;
}
